import React, { useState, useEffect, useMemo } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Network, Save, RotateCcw, Eye, Plus, Trash2, FileSearch, Info, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAGS_SAMTECH = [
    { tag: 'mot',    label: 'Motor (ignición)',     desc: 'int(1) — 0=apagado, 1=encendido' },
    { tag: 'vel',    label: 'Velocidad',             desc: 'int(3) — km/h' },
    { tag: 'hdop',   label: 'HDOP',                  desc: 'int(3) — calidad GPS (<9 OK)' },
    { tag: 'numSAT', label: 'Nº satélites',          desc: 'int(2) — 0 a 99' },
    { tag: 'ori',    label: 'Orientación',           desc: 'int(3) — grados 0-360' },
    { tag: 'odo',    label: 'Odómetro',              desc: 'decimal(6) — km recorridos' },
    { tag: 'lat',    label: 'Latitud',               desc: 'string(10) — formato -33.569855' },
    { tag: 'lon',    label: 'Longitud',              desc: 'string(10) — formato -70.569852' },
    { tag: 'sens1',  label: 'Sensor 1 (horómetro)',  desc: 'int(9) — MINUTOS (4sat→horas, usa int_x60)' },
    { tag: 'sens2',  label: 'Sensor 2',              desc: 'int(9) — campo libre' },
];

const TIPOS = [
    { value: 'int',         label: 'int (entero)' },
    { value: 'int_x60',     label: 'int ×60 (horas→minutos)' },
    { value: 'decimal',     label: 'decimal (con decimales)' },
    { value: 'bool_01',     label: 'bool → 0/1' },
    { value: 'string',      label: 'string (texto)' },
    { value: 'coord',       label: 'coord (lat/lon string)' },
    { value: 'passthrough', label: 'passthrough (sin transformar)' },
];

const OPS = [
    { value: '==',       label: '=' },
    { value: '!=',       label: '≠' },
    { value: '>',        label: '>' },
    { value: '<',        label: '<' },
    { value: '>=',       label: '≥' },
    { value: '<=',       label: '≤' },
    { value: 'contains', label: 'contiene' },
];

export default function CodelcoMapeoPage() {
    const [vehiculos, setVehiculos] = useState([]);
    const [selectedVid, setSelectedVid] = useState('');
    const [mapeo, setMapeo] = useState(null);
    const [catalogo, setCatalogo] = useState([]);
    const [diagnostico, setDiagnostico] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showDiag, setShowDiag] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [preview, setPreview] = useState(null);
    const [showReplicar, setShowReplicar] = useState(false);
    const [destinos, setDestinos] = useState([]);
    const [replicando, setReplicando] = useState(false);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

    useEffect(() => {
        const loadInit = async () => {
            try {
                const [vR, cR] = await Promise.all([
                    fetch(`${API_URL}/api/codelco/vehiculos`, { headers }),
                    fetch(`${API_URL}/api/codelco/mapeo-catalogo/eventos`, { headers }),
                ]);
                if (vR.ok) {
                    const vs = await vR.json();
                    setVehiculos(vs);
                    if (vs.length && !selectedVid) setSelectedVid(vs[0].id);
                }
                if (cR.ok) {
                    const d = await cR.json();
                    setCatalogo(d.codigos || []);
                }
            } catch { /* ignore */ }
            setLoading(false);
        };
        loadInit();
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!selectedVid) return;
        loadMapeo(selectedVid);
    // eslint-disable-next-line
    }, [selectedVid]);

    const loadMapeo = async (vid) => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/mapeo/${vid}`, { headers });
            if (r.ok) setMapeo(await r.json());
        } catch { /* ignore */ }
    };

    const loadDiagnostico = async () => {
        if (!selectedVid) return;
        toast.info('Consultando 4sat…');
        try {
            const r = await fetch(`${API_URL}/api/codelco/diagnostico-4sat?vehiculo_id=${selectedVid}`, { headers });
            const d = await r.json();
            if (d.ok) {
                setDiagnostico(d);
                setShowDiag(true);
            } else {
                toast.error(d.detail || 'Error en diagnóstico');
            }
        } catch (e) { toast.error('Error: ' + e.message); }
    };

    const loadPreview = async () => {
        if (!selectedVid) return;
        toast.info('Generando preview…');
        try {
            const r = await fetch(`${API_URL}/api/codelco/vehiculos/${selectedVid}/preview-payload`, { method: 'POST', headers });
            const d = await r.json();
            if (d.ok) {
                setPreview(d);
                setShowPreview(true);
            } else {
                toast.error(d.detail || 'Error preview');
            }
        } catch (e) { toast.error('Error: ' + e.message); }
    };

    const updateCampo = (tag, field, value) => {
        setMapeo(prev => ({
            ...prev,
            campos: { ...prev.campos, [tag]: { ...(prev.campos[tag] || {}), [field]: value } },
        }));
    };

    const updateRegla = (idx, field, value) => {
        setMapeo(prev => {
            const reglas = [...prev.reglas_eventos];
            reglas[idx] = { ...reglas[idx], [field]: value };
            return { ...prev, reglas_eventos: reglas };
        });
    };

    const addRegla = () => {
        setMapeo(prev => ({
            ...prev,
            reglas_eventos: [
                ...prev.reglas_eventos,
                { orden: prev.reglas_eventos.length + 1, campo: 'speed', op: '>', valor: '2', eve: 46, desc: '' },
            ],
        }));
    };

    const removeRegla = (idx) => {
        setMapeo(prev => ({ ...prev, reglas_eventos: prev.reglas_eventos.filter((_, i) => i !== idx) }));
    };

    const guardar = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/mapeo/${selectedVid}`, {
                method: 'PUT', headers,
                body: JSON.stringify({
                    campos: mapeo.campos,
                    reglas_eventos: mapeo.reglas_eventos,
                    eve_default: mapeo.eve_default,
                }),
            });
            if (r.ok) toast.success('Mapeo guardado correctamente');
            else toast.error('Error al guardar');
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const reset = async () => {
        if (!window.confirm('¿Restaurar mapeo default del manual Samtech para este vehículo?')) return;
        try {
            const r = await fetch(`${API_URL}/api/codelco/mapeo/${selectedVid}`, { method: 'DELETE', headers });
            if (r.ok) {
                toast.success('Mapeo restaurado a default');
                loadMapeo(selectedVid);
            }
        } catch { toast.error('Error al restaurar'); }
    };

    const replicar = async () => {
        if (!destinos.length) { toast.error('Selecciona al menos un vehículo destino'); return; }
        setReplicando(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/mapeo/${selectedVid}/replicar`, {
                method: 'POST', headers,
                body: JSON.stringify({ vehiculos_destino: destinos }),
            });
            const d = await r.json();
            if (r.ok) {
                toast.success(`Mapeo replicado a ${d.replicados} vehículo(s)`);
                setShowReplicar(false);
                setDestinos([]);
            } else {
                toast.error(d.detail || 'Error al replicar');
            }
        } catch { toast.error('Error al replicar'); }
        setReplicando(false);
    };

    const toggleDestino = (vid) => {
        setDestinos(prev => prev.includes(vid) ? prev.filter(x => x !== vid) : [...prev, vid]);
    };

    const vehiculoActual = vehiculos.find(v => v.id === selectedVid);

    return (
        <CodelcoLayout>
            <div className="space-y-6" data-testid="codelco-mapeo-page">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-2">
                            <Network className="h-7 w-7 text-amber-500" /> Mapeo XML — por vehículo
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">Define cómo se transforman los datos crudos de 4sat hacia los tags del XML Samtech.</p>
                    </div>
                </div>

                <Card>
                    <CardContent className="pt-6 space-y-4">
                        <div className="flex flex-col md:flex-row gap-3">
                            <div className="flex-1 space-y-1">
                                <Label>Vehículo a configurar</Label>
                                <Select value={selectedVid} onValueChange={setSelectedVid}>
                                    <SelectTrigger data-testid="select-vehiculo-mapeo"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                                    <SelectContent>
                                        {vehiculos.map(v => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.patente} {v.imei && `· IMEI ${v.imei}`} {v.marca && `· ${v.marca}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-2 md:items-end flex-wrap">
                                <Button variant="outline" onClick={loadDiagnostico} disabled={!selectedVid} data-testid="btn-diagnostico-4sat">
                                    <FileSearch className="h-4 w-4 mr-1" /> Diagnosticar 4sat
                                </Button>
                                <Button variant="outline" onClick={loadPreview} disabled={!selectedVid}>
                                    <Eye className="h-4 w-4 mr-1" /> Vista previa XML
                                </Button>
                                <Button variant="outline" onClick={() => setShowReplicar(true)} disabled={!selectedVid || vehiculos.length < 2} data-testid="btn-replicar-mapeo">
                                    <Copy className="h-4 w-4 mr-1" /> Replicar a otros
                                </Button>
                            </div>
                        </div>
                        {vehiculoActual && (
                            <div className="text-xs text-muted-foreground bg-muted/50 rounded p-3 flex flex-wrap gap-x-4 gap-y-1">
                                <span><b>device_id 4sat:</b> {vehiculoActual.device_id_4sat}</span>
                                <span><b>Modelo:</b> {vehiculoActual.marca || '—'}</span>
                                <span><b>Protocolo detectado:</b> consulte diagnóstico</span>
                            </div>
                        )}
                        <div className="text-xs bg-amber-500/10 border border-amber-500/30 rounded p-3 flex gap-2">
                            <Info className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                            <div>
                                <b>Recordatorio manual Codelco:</b> el campo <code>&lt;fn&gt;</code> debe ir en <b>hora local Chile</b> (UTC-3 verano / UTC-4 invierno). Configúralo en <i>Configuración → UTC offset</i>. El campo <code>&lt;pat&gt;</code> se envía siempre <b>sin guiones</b> (ej: AB1234). El campo <code>&lt;sens1&gt;</code> espera <b>minutos</b> (usa tipo <code>int_x60</code> si tu GPS reporta horas).
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {loading || !mapeo ? (
                    <Card><CardContent className="py-12 text-center text-muted-foreground">Cargando…</CardContent></Card>
                ) : (
                    <Tabs defaultValue="campos">
                        <TabsList>
                            <TabsTrigger value="campos" data-testid="tab-mapeo-campos">Mapeo de campos</TabsTrigger>
                            <TabsTrigger value="eventos" data-testid="tab-mapeo-eventos">Reglas de evento (eve)</TabsTrigger>
                        </TabsList>

                        <TabsContent value="campos">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center justify-between">
                                        <span>Campos del XML &lt;movil&gt;</span>
                                        {mapeo.es_default_campos && <Badge variant="outline">Default</Badge>}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Tag Samtech</TableHead>
                                                <TableHead>Origen 4sat (path)</TableHead>
                                                <TableHead>Tipo destino</TableHead>
                                                <TableHead>Decimales</TableHead>
                                                <TableHead>Default si vacío</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {TAGS_SAMTECH.map(({ tag, label, desc }) => {
                                                const c = mapeo.campos[tag] || {};
                                                return (
                                                    <TableRow key={tag}>
                                                        <TableCell>
                                                            <div className="font-mono font-bold">&lt;{tag}&gt;</div>
                                                            <div className="text-xs text-muted-foreground">{label}</div>
                                                            <div className="text-xs text-muted-foreground/70">{desc}</div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Input
                                                                value={c.origen || ''}
                                                                onChange={e => updateCampo(tag, 'origen', e.target.value)}
                                                                placeholder="ej: sensors.ignition"
                                                                className="font-mono text-xs"
                                                                data-testid={`mapeo-origen-${tag}`}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Select value={c.tipo || 'int'} onValueChange={v => updateCampo(tag, 'tipo', v)}>
                                                                <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                                                </SelectContent>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Input
                                                                type="number"
                                                                value={c.decimales ?? ''}
                                                                onChange={e => updateCampo(tag, 'decimales', parseInt(e.target.value || '0'))}
                                                                disabled={!['decimal', 'coord'].includes(c.tipo)}
                                                                className="w-20"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Input
                                                                value={c.default ?? ''}
                                                                onChange={e => updateCampo(tag, 'default', e.target.value)}
                                                                className="w-20 text-xs"
                                                                placeholder="0"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="eventos">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center justify-between">
                                        <span>Reglas para definir &lt;eve&gt; (manual Samtech sec. 2.6)</span>
                                        {mapeo.es_default_eventos && <Badge variant="outline">Default</Badge>}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/30 rounded p-3">
                                        <Info className="h-3.5 w-3.5 inline mr-1" />
                                        Las reglas se evalúan <b>en orden ascendente</b>. La primera que matchea define el código <code>&lt;eve&gt;</code>. Si ninguna matchea se usa el "Default si nada matchea".
                                    </div>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-16">Orden</TableHead>
                                                <TableHead>Campo (path 4sat)</TableHead>
                                                <TableHead className="w-28">Operador</TableHead>
                                                <TableHead>Valor</TableHead>
                                                <TableHead className="w-32">Código eve</TableHead>
                                                <TableHead>Descripción</TableHead>
                                                <TableHead className="w-12"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {mapeo.reglas_eventos.sort((a,b)=>(a.orden||0)-(b.orden||0)).map((r, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        <Input type="number" value={r.orden || ''} onChange={e => updateRegla(idx, 'orden', parseInt(e.target.value || '0'))} className="w-14" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={r.campo || ''} onChange={e => updateRegla(idx, 'campo', e.target.value)} placeholder="sensors.ignition" className="font-mono text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select value={r.op || '=='} onValueChange={v => updateRegla(idx, 'op', v)}>
                                                            <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={String(r.valor ?? '')} onChange={e => updateRegla(idx, 'valor', e.target.value)} placeholder="true / 2 / Movimiento" className="text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select value={String(r.eve || 46)} onValueChange={v => updateRegla(idx, 'eve', parseInt(v))}>
                                                            <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                                                            <SelectContent className="max-h-64">
                                                                {catalogo.map(c => <SelectItem key={c.cod} value={String(c.cod)}>{c.cod} — {c.nombre}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input value={r.desc || ''} onChange={e => updateRegla(idx, 'desc', e.target.value)} className="text-xs" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button variant="ghost" size="icon" onClick={() => removeRegla(idx)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 pt-3 border-t">
                                        <Button variant="outline" size="sm" onClick={addRegla} data-testid="btn-add-regla">
                                            <Plus className="h-4 w-4 mr-1" /> Agregar regla
                                        </Button>
                                        <div className="flex items-center gap-2 ml-auto">
                                            <Label className="text-xs whitespace-nowrap">Default si nada matchea:</Label>
                                            <Select value={String(mapeo.eve_default || 46)} onValueChange={v => setMapeo(prev => ({ ...prev, eve_default: parseInt(v) }))}>
                                                <SelectTrigger className="w-64 text-xs"><SelectValue /></SelectTrigger>
                                                <SelectContent className="max-h-64">
                                                    {catalogo.map(c => <SelectItem key={c.cod} value={String(c.cod)}>{c.cod} — {c.nombre}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                )}

                <div className="flex flex-col md:flex-row gap-2 justify-end sticky bottom-4">
                    <Button variant="outline" onClick={reset} disabled={!selectedVid} data-testid="btn-mapeo-reset">
                        <RotateCcw className="h-4 w-4 mr-1" /> Restaurar default
                    </Button>
                    <Button onClick={guardar} disabled={!selectedVid || saving} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-mapeo-guardar">
                        <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar mapeo'}
                    </Button>
                </div>
            </div>

            {/* Modal Diagnóstico 4sat */}
            <Dialog open={showDiag} onOpenChange={setShowDiag}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Diagnóstico 4sat — campos disponibles</DialogTitle>
                    </DialogHeader>
                    {diagnostico && (
                        <div className="space-y-4">
                            <div className="text-xs text-muted-foreground">
                                Estos son los <b>paths exactos</b> que puedes usar como "Origen 4sat" en el mapeo. Click en un path para copiarlo.
                            </div>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Path</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Muestra actual</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {diagnostico.campos_disponibles?.map((c, i) => (
                                        <TableRow key={i} className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => { navigator.clipboard.writeText(c.path); toast.success(`Copiado: ${c.path}`); }}>
                                            <TableCell className="font-mono text-xs">{c.path}</TableCell>
                                            <TableCell><Badge variant="outline" className="text-xs">{c.tipo}</Badge></TableCell>
                                            <TableCell className="text-xs truncate max-w-[200px]">{c.muestra}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Preview XML */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Vista previa del XML que se enviará a Samtech</DialogTitle>
                    </DialogHeader>
                    {preview && (
                        <div className="space-y-3">
                            <pre className="bg-zinc-900 text-zinc-100 text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap">{preview.xml}</pre>
                            <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Ver datos crudos del device 4sat</summary>
                                <pre className="bg-muted/30 p-2 mt-2 rounded text-xs overflow-x-auto">{JSON.stringify(preview.device_raw, null, 2)}</pre>
                            </details>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Modal Replicar mapeo a otros vehículos */}
            <Dialog open={showReplicar} onOpenChange={setShowReplicar}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Replicar mapeo a otros vehículos</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/30 rounded p-3">
                            <Info className="h-3.5 w-3.5 inline mr-1" />
                            Se copiará el mapeo actual de <b>{vehiculoActual?.patente}</b> (campos + reglas eve) a los vehículos seleccionados, <b>sobrescribiendo</b> el mapeo previo de cada uno.
                        </div>
                        <div className="flex justify-between items-center">
                            <Label className="text-sm">Vehículos destino</Label>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => setDestinos(vehiculos.filter(v => v.id !== selectedVid).map(v => v.id))}>Todos</Button>
                                <Button variant="ghost" size="sm" onClick={() => setDestinos([])}>Ninguno</Button>
                            </div>
                        </div>
                        <div className="max-h-72 overflow-y-auto border rounded divide-y">
                            {vehiculos.filter(v => v.id !== selectedVid).map(v => (
                                <label key={v.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid={`destino-${v.id}`}>
                                    <input
                                        type="checkbox"
                                        checked={destinos.includes(v.id)}
                                        onChange={() => toggleDestino(v.id)}
                                        className="h-4 w-4"
                                    />
                                    <div className="flex-1">
                                        <div className="font-mono font-bold text-sm">{v.patente}</div>
                                        <div className="text-xs text-muted-foreground">{v.marca || '—'} · IMEI {v.imei || '—'}</div>
                                    </div>
                                </label>
                            ))}
                            {vehiculos.filter(v => v.id !== selectedVid).length === 0 && (
                                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                    No hay otros vehículos disponibles
                                </div>
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground">{destinos.length} seleccionado(s)</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReplicar(false)}>Cancelar</Button>
                        <Button onClick={replicar} disabled={replicando || !destinos.length} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-confirmar-replicar">
                            <Copy className="h-4 w-4 mr-1" /> {replicando ? 'Replicando…' : `Replicar a ${destinos.length}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </CodelcoLayout>
    );
}
